import AccessLevelsService, {AccessLevelPackages, AccessLevelPackagesWithAggregates} from "../../../core/services/access-levels";
import React, {FunctionComponent, PropsWithChildren, useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import Api from "../../../core/services/api/api";
import FirebaseNotificationService from "../../../core/services/firebase";
import {useLocation, useRouteMatch} from "react-router-dom";
import {routes} from "../../../core/models/static/routes/app-routes";
import useIsLayoutMounted from "../../../packages/data-grid/ui/hooks/use-is-layout-mounted";
import {useAuthenticationState} from "../use-auth-state";


interface AccessLevelsServiceContextType {
    packages: AccessLevelPackagesWithAggregates;
    setPackages: (packages: DeepPartial<AccessLevelPackages>) => void;
}


const AccessLevelsServiceContext = React.createContext<AccessLevelsServiceContextType>(AccessLevelsService);


interface AccessLevelProviderProps {
    fallback: React.ReactNode;
    partnerId?: string;
    operatorId?: string;
}

/**
 * Provider to provide the access levels service to the children
 * @param props
 * @constructor
 */
export const AccessLevelsProvider: FunctionComponent<PropsWithChildren<AccessLevelProviderProps>> = (props) => {
    const {loggedIn} = useAuthenticationState();
    const [packages, _setPackages] = useState<AccessLevelsServiceContextType['packages']>();
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const isMounted = useIsLayoutMounted();
    const inAuthViews = !!useRouteMatch(routes.auth.base);
    const inLandingView = React.useMemo(() => location.pathname === routes.landing, [location.pathname]);
    const lastFetchIdentityRef = useRef<string | null>(null);
    const packagesPresentRef = useRef(false);

    packagesPresentRef.current = !!packages;

    /**
     * Set the packages in the service
     * @param packages
     */
    const setPackages = useCallback((packages: DeepPartial<AccessLevelPackages>) => {
        AccessLevelsService.setPackages(packages);
        _setPackages(AccessLevelsService.packages);
    }, [])

    const value = useMemo(() => ({
        packages: packages ?? AccessLevelsService.packages,
        setPackages,
    }), [packages, setPackages]);

    /**
     * Fetches the access levels of the user from the server api.
     * When packages are already present, re-fetches quietly without unmounting the tree.
     */
    const getAccessLevels = useCallback<(options?: {quiet?: boolean}) => Promise<void>>(async (options) => {
        const quiet = options?.quiet ?? packagesPresentRef.current;
        if (!quiet) {
            setLoading(true);
        }
        const response = await Api.getCurrentOperatorAccessLevels(props.partnerId, props.operatorId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPackages(response.data);
            if (AccessLevelsService.packages.notifications.viewList) {
                void FirebaseNotificationService.activateDevice();
            }
        }
        setLoading(false);
    }, [isMounted, setPackages, props.partnerId, props.operatorId]);

    /**
     * As soon as the component mounts / identity changes:
     * - fetches the access levels of the user.
     * Skips re-fetch when partnerId/operatorId are unchanged from the last successful identity.
     */
    useLayoutEffect(() => {
        if (!loggedIn) {
            setLoading(false);
            lastFetchIdentityRef.current = null;
            if (!inLandingView && !inAuthViews) {
                // if our starting route is not the landing page, we need to fetch the access levels of the user, even a mock one!!
                setPackages(AccessLevelsService.packages);
            }
            return;
        }
        if (inAuthViews)
            return;

        const identity = `${props.partnerId ?? ''}:${props.operatorId ?? ''}`;
        if (lastFetchIdentityRef.current === identity && packagesPresentRef.current) {
            return;
        }
        lastFetchIdentityRef.current = identity;
        getAccessLevels({quiet: packagesPresentRef.current}).then();
    }, [getAccessLevels, inAuthViews, inLandingView, setPackages, loggedIn, props.partnerId, props.operatorId])

    const needsPackages = !inLandingView && !inAuthViews;
    const missingPackages = needsPackages && !packages;

    // Only block the tree on the initial load (no packages yet). Quiet re-fetches keep children mounted.
    const shouldShowFallback = !inAuthViews && (missingPackages || (loading && !packages));

    return (
        <AccessLevelsServiceContext.Provider value={value}>
            {
                shouldShowFallback
                    ? props.fallback
                    : props.children
            }
        </AccessLevelsServiceContext.Provider>
    );
};

/**
 * Hook to access the access levels service
 */
export const useAccessLevels = () => {
    return React.useContext(AccessLevelsServiceContext);
}


export default useAccessLevels;
